/* global analytics */
import { useUserStore } from "@/stores/user";
// The one seam to the workspace (launch-marketing-pages design M2). This store
// is on every page's boot path, so it imports no workspace store: it calls the
// workspace's adapter, which is absent until a workspace mounts in the page.
import { runAfterSignIn, workspaceAdapter } from "@/services/workspaceAdapter";
// A landing as a route, with its workspace state as `?state=`: the one
// encoding for a page load and for a landing in a page no workspace has
// mounted in.
import { landingRoute } from "@/utils/workspaceRoute";
// The router's own roster of the public `/help` routes. A leaf constant
// module, so the sign-out carve-out below costs this boot-path store nothing
// but the three names (`documentation.js` reads the same roster, and importing
// THAT store here would pull the whole help corpus into auth's chunk).
import { isHelpRoute } from "@/router/helpRoutes";
// The guest-only surfaces and where a signed-in member lands (th-49c64,
// th-1tcxp). A leaf module, shared with the router's guard so an interactive
// sign-in and a navigation can never disagree about where a member belongs.
import { addressesGuestSurface, signedInLanding, startContextOf } from "@/router/guestOnlyRoutes";
import { computed, onScopeDispose, ref } from "vue";
import { storeToRefs, defineStore, acceptHMRUpdate } from "pinia";
import { useRoute, useRouter } from "vue-router";
import User from "@/stores/models/user";
import Guest from "@/stores/models/guest";
import { useApiClient } from "@/services/apiClient";
// LogRocket is loaded by the workspace (design M2, "The head"); this store
// only waits for it, so a public page never loads it.
import { vendorScriptLoaded } from "@/services/vendorScripts";
// The one owner of the session bearer (th-vfii1). Every token read and
// identity drop in this store goes through it, never `localStorage` directly.
import {
  authorizationHeaders,
  dropSessionIdentity,
  isSessionExpiredFailure,
  isTransientFailure,
  leavePage,
  readSessionToken,
} from "@/services/sessionToken";

// The boot whoami's retry budget (th-10zrs): a transient failure is asked
// again after each delay in turn — about 15 seconds across five attempts,
// enough to ride out a deploy's 502s — before the check parks at
// `unreachable`. Each attempt gives up after `WHOAMI_TIMEOUT_MS`, so a hung
// request is a retryable failure rather than a boot that never finishes.
export const WHOAMI_RETRY_DELAYS_MS = Object.freeze([1000, 2000, 4000, 8000]);
export const WHOAMI_TIMEOUT_MS = 10000;

// The one whoami path (`GET /api/v2/whoami`), read through `fetchUser`.
export const WHOAMI_PATH = "/whoami";

// How long the existing-User reset waits before it re-boots: room for the URL
// writes a re-boot WITHOUT a handed-in landing reads back to land —
// `resetWorkspaceState`'s own `router.replace` (it strips `?state=`) and the
// caller's post-sign-in navigation, behind `unifiedRouter`'s 300 ms debounce.
// A door sign-in hands its landing in, and that re-boot reads no URL back. The
// page sends nothing meanwhile (`leavePage`, th-0qphb), so the window costs a
// moment of wall time and no request.
export const SIGN_IN_REBOOT_DELAY_MS = 500;

// How long sign-out waits before it re-boots: room for its own URL write
// (the push to the map, off every route but `/help`) to land before the
// reload reads the URL back. The page sends nothing meanwhile but the
// `keepalive` session revoke (`leavePage`, th-2soni).
export const SIGN_OUT_REBOOT_DELAY_MS = 500;

export const useAuthStore = defineStore("auth", () => {
  const userStore = useUserStore();
  const { currentUser, isAdmin, signedIn } = storeToRefs(userStore);
  /**
   * WHERE THE BOOT IDENTITY CHECK STANDS (th-10zrs). The workspace renders
   * only once it is `resolved` (`identityChecked`); `retrying` and
   * `unreachable` render App.vue's reconnect notice instead — never a guest
   * shell over a session nobody ended.
   *
   *   `checking`    — the first whoami is in flight;
   *   `retrying`    — a whoami failed transiently and the next attempt is
   *                   backing off;
   *   `unreachable` — the retries ran out (or the failure was not worth
   *                   retrying); the bearer is still held, and the next
   *                   attempt waits for the member's "Try again" or the
   *                   browser's `online` event;
   *   `resolved`    — whoami answered, and this tab is its user or a guest.
   */
  const identityStatus = ref("checking");
  const identityChecked = ref(false);
  const identityInterrupted = computed(
    () => identityStatus.value === "retrying" || identityStatus.value === "unreachable",
  );

  // One identity check at a time: the "Try again" button and the `online`
  // event can both fire while an attempt is already in flight.
  let identityCheck = null;

  // Settles the moment `identityStatus` reaches `resolved`, and never before
  // (th-49c64). `unreachable` is not an answer, because the member may still be
  // signed in. So a reader that must know WHO this tab is waits through the
  // reconnect notice until whoami finally answers.
  let announceIdentityResolved;
  const identityResolved = new Promise((resolve) => {
    announceIdentityResolved = resolve;
  });

  function markIdentityResolved() {
    identityStatus.value = "resolved";
    identityChecked.value = true;
    announceIdentityResolved();
  }

  /**
   * The boot identity check: ask whoami who this tab is, and adopt the answer.
   *
   * ONLY `401 session_expired` ends a session, and it never reaches this
   * function's recovery: the API client has already handed it to
   * `sessionToken.js#handleSessionExpired`, which drops the bearer and is
   * leaving the page. Every other failure keeps the bearer (th-10zrs) — a
   * transient one (`isTransientFailure`: network, timeout, 5xx) is retried on
   * `WHOAMI_RETRY_DELAYS_MS`, and when those run out, or the failure was not
   * worth retrying, the check parks at `unreachable` instead of signing the
   * member out or reloading into the same failure. A guest holds no bearer,
   * so a guest's failed whoami boots the bare `Guest` at once, as it always has.
   *
   * ONCE PER PAGE LOAD (th-49c64). Once the identity has resolved, this is a
   * no-op. App.vue's boot call and the router guard's wait
   * (`whenIdentityResolved`) can each be first, and whichever one comes second
   * must not ask whoami again.
   */
  function getCurrentUser() {
    if (identityStatus.value === "resolved") return Promise.resolve();
    if (!identityCheck) {
      identityCheck = resolveIdentity().finally(() => {
        identityCheck = null;
      });
    }
    return identityCheck;
  }

  async function resolveIdentity() {
    for (let attempt = 0; ; attempt += 1) {
      let fetchedUser;
      try {
        fetchedUser = await fetchUser({ signal: AbortSignal.timeout(WHOAMI_TIMEOUT_MS) });
      } catch (error) {
        if (isSessionExpiredFailure(error)) return;

        if (!readSessionToken()) {
          markIdentityResolved();
          return;
        }

        if (!isTransientFailure(error) || attempt >= WHOAMI_RETRY_DELAYS_MS.length) {
          identityStatus.value = "unreachable";
          window.addEventListener("online", retryIdentityCheck, { once: true });
          return;
        }

        identityStatus.value = "retrying";
        await new Promise((resolve) => setTimeout(resolve, WHOAMI_RETRY_DELAYS_MS[attempt]));
        continue;
      }

      adoptWhoami(fetchedUser);
      return;
    }
  }

  function adoptWhoami(fetchedUser) {
    if (fetchedUser.signedIn) {
      signIn(new User(fetchedUser));
    } else {
      // A 200 guest envelope while a bearer is held is the server's verdict
      // that the bearer is no session at all (a JWT-shaped dead one is
      // answered `401 session_expired` instead): drop that identity the way
      // a dead session does, then boot as the guest the server answered.
      if (readSessionToken()) dropSessionIdentity();
      // Guest user - store 'guest' as the user ID for tracking
      const guestUser = new Guest(fetchedUser)
      if (!localStorage.getItem('tower_hunt_last_user_id')) {
        localStorage.setItem('tower_hunt_last_user_id', 'guest')
      }
      signIn(guestUser);
    }
    markIdentityResolved();
  }

  /**
   * Resolves once whoami has answered who this tab is (th-49c64): the router
   * guard's wait before it decides whether a guest-only surface admits this
   * tab. If the boot check has not started yet, this starts it; a check already
   * in flight is joined. Nothing here retries from `unreachable`: that belongs
   * to the reconnect notice, and this simply keeps waiting.
   */
  function whenIdentityResolved() {
    if (identityStatus.value === "checking") getCurrentUser();
    return identityResolved;
  }

  /**
   * Ask whoami again from `unreachable` — the reconnect notice's "Try again"
   * and the browser's `online` event. A no-op once the identity resolved.
   */
  function retryIdentityCheck() {
    if (identityStatus.value !== "unreachable") return identityCheck;
    window.removeEventListener("online", retryIdentityCheck);
    identityStatus.value = "retrying";
    return getCurrentUser();
  }

  // A parked check's `online` listener goes with the store (HMR, tests).
  onScopeDispose(() => window.removeEventListener("online", retryIdentityCheck));

  /**
   * Ask whoami who this tab is; resolves to the session envelope itself
   * (`signedIn` and the rest at the top level). `options` are the v2
   * client's: the boot check passes its timeout as an abort `signal`.
   * `silent403`: a refused whoami is the identity check's to answer (it
   * parks at `unreachable`), never a "Permission denied" toast.
   */
  async function fetchUser(options = {}) {
    return useApiClient().get(WHOAMI_PATH, { ...options, silent403: true });
  }

  const router = useRouter();
  const route = useRoute();

  /**
   * Adopt `userModel` as the current identity, dispatching the `authTransition`
   * every user-scoped store listens for.
   *
   * The workspace-reset ritual (see `shouldResetWorkspace` below) turns on the
   * two options:
   *
   *   `interactive` — TRUE only when a person just authenticated in this tab.
   *     The boot paths (`getCurrentUser`) also reach `signIn`, and can even
   *     compute a `guestToUser` transition — a shared browser where another tab
   *     signed in leaves `tower_hunt_last_user_id` on 'guest' while a token
   *     exists. Booting is not logging in, and the URL being opened is the
   *     user's own (a bookmark, a shared link), so those paths reset nothing.
   *
   *   `isNewRegistration` — TRUE when the guest just BECAME this User. A brand
   *     new account has no workspace of its own, so the guest's map, filters
   *     and pinned set are the only state in existence and it is theirs to
   *     keep. An EXISTING User signing in is the opposite case: the guest
   *     workspace belongs to whoever was browsing before them, and carrying it
   *     across the identity boundary is the bug this ritual closes.
   *
   * Both default to false so every non-authenticating caller (boot, invite-link
   * flows) keeps its current behavior.
   *
   *   `landing` — the sign-in door's `{ startContext, invitations }` (th-1tcxp):
   *     the route it opened on and the answer's invitation outcomes. The
   *     member lands where `signedInLanding` resolves from them, on the re-boot
   *     and without one, never where the URL stands by then. Absent, the
   *     landing resolves from the current route, as it always has.
   *
   * @param {User} userModel
   * @param {{ interactive?: boolean, isNewRegistration?: boolean,
   *           landing?: { startContext: object|null, invitations?: Array } }} [options]
   */
  async function signIn(userModel, { interactive = false, isNewRegistration = false, landing = null } = {}) {
    // Handle cache clearing on user transitions
    const previousUserId = localStorage.getItem('tower_hunt_last_user_id')
    const currentUserId = String(userModel.id || 'guest')

    // Determine transition type before mutating state so we can dispatch with
    // the new userModel already in place (see below).
    let transitionType = null
    if (previousUserId === 'guest' && currentUserId !== 'guest') {
      transitionType = 'guestToUser'
    } else if (previousUserId && previousUserId !== currentUserId && currentUserId !== 'guest') {
      transitionType = 'userSwitch'
    }

    // Registration rides the SAME `type` as any other guest->user transition.
    // The discriminator is a sibling field, not a new type value, because every
    // identity-scoped listener (localStorageCache's band caches, filteredData,
    // layerData, artemisInbox, the balance channel, push subscriptions) must
    // fire for a new registrant exactly as it does for a returning one — the
    // actor changed, so every access-banded payload is stale either way. Only
    // the workspace reset below distinguishes the two, and it reads this field.

    // Reset when a deliberate act changed WHO this tab is. Each clause earns
    // its place:
    //
    //   `interactive`          — excludes the boot paths, which also reach
    //                            `signIn` and can even compute a `guestToUser`
    //                            on a shared browser.
    //   `!isNewRegistration`   — the guest BECAME this User; the workspace is
    //                            theirs to keep.
    //   `currentUserId` guard  — adopting the Guest identity is `signOut`'s job.
    //   identity comparison    — excludes re-adopting the same account.
    //
    // Deliberately compared against the RAW marker rather than `transitionType`.
    // A cold tab whose guest whoami failed carries no marker at all — only an
    // answered guest envelope writes 'guest' (`adoptWhoami`) — so a member who
    // signs in there reads as a null previous id. That differs from the incoming
    // id, which is the right answer: the tab's workspace is whoever browsed it
    // before, not theirs. `transitionType` needs a non-null marker for either of
    // its branches and would call that identity change no change at all.
    const shouldResetWorkspace =
      interactive &&
      !isNewRegistration &&
      currentUserId !== 'guest' &&
      previousUserId !== currentUserId

    if (currentUserId !== 'guest') {
      localStorage.setItem('tower_hunt_last_user_id', currentUserId)
    }

    // Resolved once, from what the door captured: every token in it was posted
    // with the proof, so the answer's outcomes say what each one became.
    const resolvedLanding = landing
      ? signedInLanding(landing.startContext, landing.invitations ?? [])
      : null

    // THIS PAGE LEAVES BEFORE ANYTHING HEARS OF THE NEW IDENTITY (th-0qphb).
    // The reset below ends in a re-boot, so from here on the page sends
    // nothing (`leavePage`). The transition's listeners and every watcher on
    // `currentUser` still run, and still CLEAR what they cached under the old
    // identity — the band caches live in `localStorage` and outlive the page,
    // so that clearing is the part the next boot depends on — but none of
    // their refetches is sent. Each would be a full server read (the map's
    // `/filtered_data/query` among them), captured and charged at read time,
    // for a page nobody will see; the re-booted page reads everything once,
    // as this User. Declared BEFORE `currentUser` moves, because some
    // listeners fetch inside the dispatch itself.
    if (shouldResetWorkspace) {
      leavePage(() => rebootSignedIn(resolvedLanding), { afterMs: SIGN_IN_REBOOT_DELAY_MS });
    }

    // Set the new user BEFORE dispatching authTransition. Listeners that
    // promote user-scoped preferences (e.g. filterCriteria.geographyBounds
    // pulling from currentUser.defaultGeographyMode) need to see the new
    // user when the event fires; existing listeners that only clear local
    // state are unaffected by the ordering.
    currentUser.value = userModel;

    if (transitionType) {
      window.dispatchEvent(new CustomEvent('authTransition', {
        detail: { type: transitionType, previousUserId, currentUserId, isNewRegistration }
      }))
    }

    // The existing-User reset, mirroring `signOut` below: blank the workspace,
    // then reload so the app re-boots as this User with nothing carried over.
    //
    // Why a reload rather than an in-SPA teardown. Per-store `authTransition`
    // listeners each clear their own slice, and their coverage is exactly what
    // cannot be trusted here — a store that never registered one (the shared
    // pinned set was one) leaks the previous browser's work silently. A reload
    // is complete by construction, is already how sign-out behaves, and costs
    // nothing extra: every banded read has to be refetched across an identity
    // change regardless.
    //
    // The workspace's `resetState` runs FIRST and synchronously, so any URL
    // write that follows composes from defaults instead of re-encoding the
    // guest's workspace. With no workspace in the page there is nothing to
    // reset.
    //
    // THE RE-BOOT LOADS THE LANDING (th-49c64, th-1tcxp). A door sign-in hands
    // its landing in, and the re-boot loads exactly what `signedInLanding`
    // resolved from it, whatever the URL says by then. Without one, a reload
    // re-opens the URL the tab stands on, which on a guest-only surface (the
    // standalone `/sign-in` that a dead session is sent to) would re-open the
    // sign-in form for a signed-in member; so `rebootSignedIn` reloads only a
    // page the member belongs on, and otherwise loads `signedInLanding`, the
    // same destination the router's guard picks.
    //
    // It RETURNS here. The post-sign-in reads below (task lists, company
    // memberships) belong to the page that boots next, whose own `signIn` runs
    // them as this User. Here they would never be sent, so awaiting them would
    // hold the caller's own navigation forever, and the re-boot would read back
    // a URL that navigation never wrote.
    if (shouldResetWorkspace) {
      try {
        workspaceAdapter()?.resetState();
      } catch (error) {
        console.warn("[Auth] Failed to reset workspace state on sign-in:", error);
      }

      setAnalytics();
      return;
    }

    // No reset (a new registration, or re-adopting the same account), so no
    // re-boot either. Signing in is not a navigation, so the guard never sees
    // it, and the tab lands here.
    if (interactive && currentUserId !== 'guest') {
      if (resolvedLanding) {
        landInPlace(resolvedLanding);
      } else if (addressesGuestSurface(route)) {
        router.replace(signedInLanding(startContextOf(route)).location);
      }
    }

    setAnalytics();
    // The workspace's post-adoption reads (the task lists). A page no
    // workspace has mounted in, a guest's Home load among them, runs them when
    // one mounts.
    await runAfterSignIn();
    if (isAdmin.value) {
      return "admin";
    } else if (signedIn.value) {
      await userStore.fetchCompanyMemberships();
    }
  }

  /**
   * The existing-User reset's full page load: load the resolved landing when
   * the sign-in handed one in (th-1tcxp); otherwise reload a page this member
   * belongs on, and load the signed-in landing in place of a guest-only one
   * (th-49c64).
   *
   * @param {{ location: object|string, workspaceState: object|null }|null} [landing]
   */
  function rebootSignedIn(landing = null) {
    if (landing) {
      location.assign(landingHref(landing));
    } else if (addressesGuestSurface(route)) {
      location.assign(router.resolve(signedInLanding(startContextOf(route)).location).href);
    } else {
      location.reload();
    }
  }

  // The landing as a URL for a fresh page load: its location, with the
  // workspace state it opens encoded as the `?state=` param the booting
  // workspace reads.
  function landingHref(landing) {
    return router.resolve(landingRoute(landing)).href;
  }

  // The landing without a page load. Once a workspace has mounted in this page
  // the member keeps it (a new registration keeps what the guest built, even
  // from `/sign-in` or a public page's band), so the landing's workspace state
  // is merged into it in place (a `?state=` URL would reset every section it
  // does not name), and the tab moves to the location, which carries no
  // consumed token. With none ever mounted (a cold public page's band) there
  // is nothing to keep and nothing to merge into: the tab moves to the landing
  // with its state on the address, and the workspace that mounts reads it as a
  // page load does. Never a wait for a mount only this move would cause.
  function landInPlace(landing) {
    const workspace = workspaceAdapter();
    if (!workspace) {
      router.replace(landingRoute(landing));
      return;
    }
    if (landing.workspaceState) workspace.applyLanding(landing.workspaceState);
    router.replace(landing.location);
  }

  function setAnalytics() {
    if (currentUser.value.id) {
      // Identifies whoever is signed in once LogRocket has loaded; resolves
      // `null`, identifying no one, where the page records nothing.
      vendorScriptLoaded("logrocket").then((LogRocket) => {
        const user = currentUser.value;
        if (!LogRocket || !user.id) return;
        LogRocket.identify(user.id, {
          name: user.name,
          email: user.email,
          createdAt: user.userCreatedAt,
        });
      });

      if (analytics) {
        analytics.identify(currentUser.value.id, {
          name: currentUser.value.name,
          email: currentUser.value.email,
          createdAt: currentUser.value.userCreatedAt,
          signupMonthYear: currentUser.value.signupMonthYear,
        });
      }
    }
  }

  /**
   * Sign-out ends the session on the SERVER, not just in this tab (th-vfii1):
   * `DELETE /api/v2/session` revokes every bearer and cable ticket the member
   * holds, on every device. It presents the bearer being ended explicitly —
   * the tab has already dropped it — and rides `keepalive`, so the departure
   * gate sign-out has already closed lets it out and the reload that follows
   * cannot abort it (th-2soni). Best-effort: a failed revoke leaves this
   * tab signed out regardless, and the bearer still expires on its own.
   */
  function revokeServerSession(token) {
    if (!token) return;
    useApiClient()
      .baseFetch("/session", {
        method: "DELETE",
        headers: authorizationHeaders(token),
        keepalive: true,
      })
      .catch(() => {});
  }

  async function signOut() {
    // The bearer this tab is ending, captured before the identity drops.
    const endingToken = readSessionToken();

    // THIS PAGE LEAVES BEFORE ANYTHING HEARS OF THE SIGN-OUT (th-2soni) — the
    // same departure gate an existing-User sign-in and a dead session declare
    // (th-0qphb). Sign-out ends in a reload, so from here on the page sends
    // nothing but a `keepalive` teardown. The `signOut` transition's listeners
    // and every watcher on `currentUser` still CLEAR what they cached as the
    // member, but none of their guest refetches is sent: each would be a full
    // server read (the map's `/filtered_data/query` among them) for a page
    // nobody will see. The rebooted page reads everything once, as a guest —
    // its boot whoami builds the `Guest`, team contact roster included.
    // Declared BEFORE the identity drop, because some listeners fetch inside
    // the dispatch itself.
    leavePage(() => location.reload(), { afterMs: SIGN_OUT_REBOOT_DELAY_MS });

    // The bearer, the last-user marker, and the `signOut` authTransition every
    // user-scoped store clears on — the same drop a dead session takes.
    dropSessionIdentity();

    // The revoke rides `keepalive`, so the departure gate lets it through
    // and the reload cannot abort it.
    revokeServerSession(endingToken);

    // Identity leaves SYNCHRONOUSLY: every `signedIn` reader in the app is
    // downgraded on this tick, before anything awaits. The bare `Guest` lives
    // only until the reload; the rebooted page's boot whoami builds the one
    // that carries the team contact roster.
    currentUser.value = new Guest();

    // The member's workspace leaves with them, the same reset an existing-User
    // sign-in takes (th-xghjd): a member-only panel (Me ▸ Settings, its
    // `meTab.settingsPage`) must not stay on screen for the guest across the
    // reboot delay, nor be re-encoded into the URL the rebooted page reads. It
    // runs FIRST and synchronously, so the Home push below and any section
    // write after it compose from defaults; then the panels close. With no
    // workspace in the page there is nothing to reset.
    const workspace = workspaceAdapter();
    try {
      workspace?.resetState();
    } catch (error) {
      console.warn("[Auth] Failed to reset workspace state on sign-out:", error);
    }

    workspace?.resetPanels();

    // Signing out on a public help page leaves the member on the page they
    // were reading: the help center IS the corpus and needs no account, so
    // all three `/help` routes are equally a valid place to be a guest.
    // Anywhere else, a signed-out session lands on Home, and the reboot
    // reloads there (launch-marketing-pages M1).
    if (!isHelpRoute(route.name)) {
      router.push({ name: "Home" });
    }
  }

  return {
    identityStatus,
    identityChecked,
    identityInterrupted,
    fetchUser,
    getCurrentUser,
    whenIdentityResolved,
    retryIdentityCheck,
    signIn,
    signOut,
    setAnalytics,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
